<script setup>
import { useRouter } from 'vue-router'
import { isAuthenticated } from '../auth/authSession.js'
import AppButton from '../../shared/components/AppButton.vue'

const router = useRouter()
const guideCards = [
  { number: '01', manualAnchor: 'coverage-pdf', title: '보험사 보장분석 PDF 업로드', text: '가입한 보험의 보장 내용을 한곳에 모읍니다.' },
  { number: '02', manualAnchor: 'health-checkup-pdf', title: '국민건강보험공단 건강검진 PDF 업로드', text: '건강검진 결과를 분석의 기준으로 연결합니다.' },
  { number: '03', manualAnchor: 'coverage-excel', title: '기준표 Excel 업로드', text: '필요한 보장 기준을 분석에 함께 반영합니다.' },
  { number: '04', manualAnchor: 'report', title: '건강 × 보장 통합 리포트 생성', text: '어디가, 왜 부족한지 한눈에 확인합니다.' }
]

function startAnalysis() {
  router.push(isAuthenticated.value ? '/upload' : '/login?redirect=/upload')
}
</script>

<template>
  <section class="home-page">
    <!-- 히어로: 홀로그램이 주인공 -->
    <div class="home-hero">
      <div class="home-hero-inner">
        <div class="home-hero-copy">
          <p class="home-kicker">건강검진 × 보험 보장</p>
          <h1>보험이 부족합니다가<br>아니라 <em>어디가, 왜</em><br>부족한지 보여 드립니다.</h1>
          <p class="home-lead">검진 결과와 보장분석 자료를 올리면, 몸의 위치별로 부족한 보장을 그림과 표로 정리한 리포트를 받습니다.</p>
          <div class="home-hero-actions">
            <AppButton size="lg" variant="light" @click="startAnalysis">분석 시작하기</AppButton>
            <a class="app-button app-button--lg app-button--outline" href="/manual/viewer.html" target="_blank" rel="noopener">사용 안내</a>
          </div>
        </div>
        <div class="holo-stage is-image" aria-hidden="true">
          <picture><source media="(min-width: 1100px)" srcset="/holo/holo-hd.webp"><img class="holo-img" src="/holo/holo.webp" alt="" width="560" height="920" fetchpriority="high"></picture>
          <span class="holo-scan" />
          <div class="holo-callout is-right is-on static-callout"><b>건강 × 보장 <small>통합 분석</small></b><p>검진 결과와 보장을 함께 확인</p><div class="holo-bar"><i style="--w: 60%" /><span>예시</span></div></div>
          <p class="holo-caption">※ 설명을 위한 예시 화면입니다. 실제 결과는 리포트에서 확인하세요.</p>
        </div>
        <dl class="home-hero-strip">
          <div><dt>교차 분석</dt><dd>검진 결과와 보장 내용을 함께</dd></div>
          <div><dt>몸의 위치로</dt><dd>어디가 부족한지 그림으로</dd></div>
          <div><dt>PDF 리포트</dt><dd>화면에서 보고 바로 다운로드</dd></div>
        </dl>
      </div>
    </div>

    <!-- 자료 한 줄 -->
    <p class="home-sources reveal is-in">이런 자료로 분석합니다 — <b>건강검진 결과</b> <small>국민건강보험공단</small> · <b>보험 보장분석</b> <small>KB손해보험</small> · <b>권장 보장금액 기준표</b> <small>Excel · 선택</small></p>

    <!-- 진행 4장면 -->
    <div id="how-it-works" class="home-steps page-container">
      <div class="section-heading reveal is-in">
        <h2>이렇게 진행됩니다</h2>
        <p class="lead">장면을 누르면 단계별 사용 안내가 열립니다.</p>
      </div>
      <div class="scenes">
        <a v-for="card in guideCards" :key="card.number" class="scene reveal is-in" :href="`/manual/viewer.html#${card.manualAnchor}`" target="_blank" rel="noopener">
          <div class="scene-photo" :class="`is-${Number(card.number)}`" :style="{ backgroundImage: `url(/photos/step${Number(card.number)}.webp)` }">
            <span class="scene-num">{{ Number(card.number) }}</span>
            <span class="scene-chip"><b>{{ card.title }}</b><small>단계별 사용 안내 ↗</small></span>
          </div><h3>{{ card.title }}</h3><p>{{ card.text }}</p>
        </a>
      </div>
    </div>

    <!-- 리포트 4장 -->
    <div class="home-features">
      <div class="page-container home-features-inner">
        <div class="feature-list">
          <article id="feature-1" class="feature reveal is-in"><div class="feature-copy"><span class="feature-num">01</span><h2>최근 건강검진을 한눈에</h2><p>검진 원문의 검사값과 소견을 그대로 옮겨, 추적검사가 필요한 곳과 양호한 곳을 색과 글자로 나눕니다.</p></div><figure class="feature-figure"><img src="../../assets/home/f1.webp" alt="리포트 예시 — 건강검진 요약" loading="lazy"><figcaption>리포트 예시 — 건강검진 요약</figcaption></figure></article>
          <article id="feature-2" class="feature reveal is-in is-flip"><div class="feature-copy"><span class="feature-num">02</span><h2>몸의 위치로 봅니다</h2><p>사람 몸 그림 위에 번호로 표시합니다. 글자만 읽지 않아도 어디가 문제인지 보입니다.</p></div><figure class="feature-figure"><img src="../../assets/home/f2.webp" alt="리포트 예시 — 몸의 위치 지도" loading="lazy"><figcaption>리포트 예시 — 몸의 위치 지도</figcaption></figure></article>
          <article id="feature-3" class="feature reveal is-in"><div class="feature-copy"><span class="feature-num">03</span><h2>내 보험으로 본 보장 현황</h2><p>건강 영역마다 관련 보장을 골라, 어느 보험에서 얼마를 보장받는지와 권장 금액 대비 부족분을 보여 드립니다.</p></div><figure class="feature-figure"><img src="../../assets/home/f3.webp" alt="리포트 예시 — 보장 현황" loading="lazy"><figcaption>리포트 예시 — 보장 현황</figcaption></figure></article>
          <article id="feature-4" class="feature reveal is-in is-flip"><div class="feature-copy"><span class="feature-num">04</span><h2>질환별 건강과 보장</h2><p>검진 소견, 같은 질환의 공개 통계, 관련 보장을 한 장에서 함께 봅니다.</p></div><figure class="feature-figure"><img src="../../assets/home/f4.webp" alt="리포트 예시 — 질환별 페이지" loading="lazy"><figcaption>리포트 예시 — 질환별 페이지</figcaption></figure></article>
        </div>
        <ol class="feature-toc" aria-label="리포트 구성">
<li><b>01</b><a href="#feature-1">최근 건강검진을 한눈에</a></li>
<li><b>02</b><a href="#feature-2">몸의 위치로 봅니다</a></li>
<li><b>03</b><a href="#feature-3">내 보험으로 본 보장 현황</a></li>
<li><b>04</b><a href="#feature-4">질환별 건강과 보장</a></li>
        </ol>
      </div>
    </div>

    <!-- 한 문장 + 포함 내용 -->
    <div class="home-band" style="background-image: url(/photos/band.webp)">
      <div class="page-container">
        <p class="band-kicker reveal is-in">자료 세 가지, 리포트 한 권</p>
        <h2 class="band-title reveal is-in">검진 결과와 보장 내용을<br>한 번에, 한 장으로.</h2>
      </div>
    </div>
    <div class="home-included page-container">
      <dl class="included-list reveal is-in">
        <div><dt>건강 영역별 상태</dt><dd>추적검사 필요 · 주의 · 양호 · 자료 없음</dd></div>
<div><dt>주요 검사값</dt><dd>참고범위와 검진기관 소견 그대로</dd></div>
<div><dt>보장항목별 비교</dt><dd>보유 금액 · 권장 금액 · 과부족 · 가입 상품별 금액</dd></div>
<div><dt>질환별 통계</dt><dd>공개 통계와 관련 보장을 한 장에</dd></div>
<div><dt>먼저 확인할 보장</dt><dd>할 일 목록으로 정리</dd></div>
<div><dt>PDF 리포트</dt><dd>화면에서 보고 결과이력에서 다시 받기</dd></div>
      </dl>
      <div class="included-actions reveal is-in">
        <AppButton size="lg" @click="startAnalysis">분석 시작하기</AppButton>
        <AppButton size="lg" variant="secondary" @click="router.push('/history')">결과이력</AppButton>
      </div>
    </div>

    <!-- 자주 묻는 질문 -->
    <div class="home-faq page-container">
      <h2 class="reveal is-in">자주 묻는 질문</h2>
      <div class="faq-list reveal is-in">
<details class="faq" open><summary><span>어떤 자료가 필요한가요?</span><i aria-hidden="true" /></summary><p>건강검진 결과 PDF와 보험 보장분석 PDF 두 가지가 필요합니다. 권장 보장금액 기준표(Excel)는 선택입니다.</p></details>
<details class="faq"><summary><span>어느 보험사 자료가 되나요?</span><i aria-hidden="true" /></summary><p>지금은 KB손해보험 전산의 보장분석 결과지를 기준으로 분석합니다. 다른 보험사는 차례로 넓혀 갈 예정입니다.</p></details>
<details class="faq"><summary><span>분석은 얼마나 걸리나요?</span><i aria-hidden="true" /></summary><p>파일 크기에 따라 다르지만 보통 몇 분 안에 끝납니다. 끝나면 결과 화면으로 자동으로 넘어갑니다.</p></details>
<details class="faq"><summary><span>결과를 나중에 다시 볼 수 있나요?</span><i aria-hidden="true" /></summary><p>네. 상단 메뉴의 &quot;결과이력&quot;에서 지금까지 산출한 결과를 보고 PDF로 다시 받을 수 있습니다.</p></details>
<details class="faq"><summary><span>리포트가 진단이나 가입 권유인가요?</span><i aria-hidden="true" /></summary><p>아닙니다. 검진 결과와 보장 자료를 함께 정리한 참고 자료이며, 진단·보험금 지급·가입 권유를 뜻하지 않습니다.</p></details>
      </div>
    </div>

    <footer class="app-footer">
      <div class="app-footer-inner">
        <div><b>MIDA</b><span>Medical × Insurance Data Analytics</span></div>
        <nav class="footer-links" aria-label="바닥 메뉴"><RouterLink to="/upload">분석 시작</RouterLink><RouterLink to="/history">결과이력</RouterLink><a href="/manual/viewer.html" target="_blank" rel="noopener">사용 안내</a></nav>
        <div class="footer-note">분석 결과는 참고 자료이며, 진단이나 보험 가입 권유가 아닙니다.</div>
      </div>
    </footer>


  </section>
</template>